import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownWideShort, faArrowUpWideShort } from '@fortawesome/free-solid-svg-icons';

export type SortOrder = 'newest' | 'oldest';

type Props = {
  menuId: string;
  sortOrder: SortOrder;
  labels: Record<SortOrder, string>;
  onSortChange: (order: SortOrder) => void;
};

const SORT_ORDERS: SortOrder[] = ['newest', 'oldest'];

export const SortOrderMenu: React.FC<Props> = ({ menuId, sortOrder, labels, onSortChange }: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls={menuId}
        className="flex items-center gap-1.5 text-xs text-black hover:text-gray-800"
        onClick={() => setMenuOpen((v) => !v)}
      >
        <FontAwesomeIcon icon={sortOrder === 'oldest' ? faArrowUpWideShort : faArrowDownWideShort} />
        <span>{labels[sortOrder]}</span>
      </button>
      {menuOpen && (
        <div
          id={menuId}
          role="menu"
          className="absolute left-0 top-full z-10 mt-1 min-w-[140px] rounded-lg bg-white py-1 shadow-lg"
        >
          {SORT_ORDERS.map((order) => (
            <button
              key={order}
              type="button"
              role="menuitemradio"
              aria-checked={sortOrder === order}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-black hover:bg-gray-200"
              onClick={() => {
                onSortChange(order);
                setMenuOpen(false);
              }}
            >
              {labels[order]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
